import { useState, useCallback } from "react";
import ExportService from "@/services/export";
import {
  ExportFormat,
  ExportProgress,
  ExportRequest,
  ExportResult,
} from "@/types/export";
import { toast } from "react-toastify";

interface UseExportReturn {
  progress: ExportProgress;
  isExporting: boolean;
  exportData: (
    data: any[],
    format: ExportFormat,
    filename?: string,
  ) => Promise<ExportResult>;
  resetProgress: () => void;
}

const useExport = (): UseExportReturn => {
  const [progress, setProgress] = useState<ExportProgress>(
    ExportService.getInitialProgress(),
  );

  const isExporting =
    progress.status === "preparing" ||
    progress.status === "processing" ||
    progress.status === "downloading";

  const handleProgress = useCallback((newProgress: ExportProgress) => {
    setProgress(newProgress);
  }, []);

  const exportData = useCallback(
    async (
      data: any[],
      format: ExportFormat,
      filename: string = "export",
    ): Promise<ExportResult> => {
      try {
        const request: ExportRequest = {
          data,
          format,
          filename,
          config: {
            includeHeaders: true,
            maxRecords: 10000,
          },
        };

        const validation = ExportService.validateExportRequest(request);
        if (!validation.valid) {
          const errorMessage = validation.error || "Invalid export request";
          toast.error(errorMessage);
          setProgress({
            status: "error",
            totalRecords: 0,
            processedRecords: 0,
            percentage: 0,
            error: errorMessage,
          });
          return {
            success: false,
            filename,
            recordsExported: 0,
            error: errorMessage,
          };
        }

        if (validation.error) {
          toast.warning(validation.error);
        }

        const result = await ExportService.exportData(request, handleProgress);

        if (result.success) {
          toast.success(
            `Successfully exported ${result.recordsExported} records`,
          );
        } else {
          toast.error(result.error || "Export failed");
        }

        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Unexpected error during export";

        setProgress({
          status: "error",
          totalRecords: 0,
          processedRecords: 0,
          percentage: 0,
          error: errorMessage,
        });

        toast.error(errorMessage);

        return {
          success: false,
          filename,
          recordsExported: 0,
          error: errorMessage,
        };
      }
    },
    [handleProgress],
  );

  const resetProgress = useCallback(() => {
    setProgress(ExportService.getInitialProgress());
  }, []);

  return {
    progress,
    isExporting,
    exportData,
    resetProgress,
  };
};

export default useExport;
